import { useCallback } from "react";
import useSWR from "swr";
import useUser from "../context/userHook";

import useTokenHook from "../context/useTokenHook";
import { fetchUsage, fetchUsageWithRefresh } from "../../utils/requests/usageRequest";
import useRevalidateOnAddItemCompleted from "../ui/useRevalidateOnAddItemCompleted";

/**
 * Matches the usage cache's fresh window in simb-app-ms: within it the backend
 * serves the cached value without recalculating, so asking more often than
 * this cannot return anything new.
 */
const USAGE_DEDUPING_INTERVAL_MS = 60_000;

const useUsageData = () => {
  const { userId } = useUser();

  const { token } = useTokenHook();

  // Opening the usage UI is a refresh trigger: the cached value paints now and,
  // if it was stale, a recalculation runs server-side for the next visit.
  const { isLoading, error, data, mutate, isValidating } = useSWR(
    token && userId ? `usageData:${userId}-` : null,
    () => fetchUsageWithRefresh(token!),
    { revalidateOnFocus: false, dedupingInterval: USAGE_DEDUPING_INTERVAL_MS },
  );

  // A sync that just finished must show up at once, and the cache may still be
  // inside its fresh window — so this one path bypasses it and reads live.
  const showLiveCountsAfterSync = useCallback(
    () => (token ? mutate(fetchUsage(token), { revalidate: false }) : mutate()),
    [mutate, token],
  );

  useRevalidateOnAddItemCompleted(showLiveCountsAfterSync);

  return {
    isLoading,
    error,
    usageData: data || null,
    mutateUsage: mutate,
    isValidating,
  };
};

export default useUsageData;
